/**
 * Fund Investor Calculator
 *
 * For fund investors (like Maguire Hayden) where value is based on
 * Capital Account (Contributions - Redemptions - Returns of Capital)
 * plus Distributions and Returns of Capital received.
 *
 * Total Value = Capital Account + Distributions + Returns of Capital
 * (RoC is treated as realized value, similar to distributions)
 */

const FundInvestorCalculator = {
	methodId: 'fund_investor_capital_account',
	displayName: 'Fund Investor (Capital Account)',
	requiredDataSources: ['contributions', 'distributions', 'capitalAccount', 'returnsOfCapital'],

	// Graph configuration - 2-bar chart best shows fund performance (PIC vs Total Value)
	graphConfig: {
		recommendedType: 'comparison_bars',
		timeSeriesStrategy: 'fund_capital_account',
		supportsTimeSeries: true,
	},

	/**
	 * Calculate fund investor value
	 * @param {Object} input
	 * @param {Object} input.holdings - { contributions, distributions, capitalAccount, returnsOfCapital } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings;
		const shareClasses = input.shareClasses || Object.keys(holdings.contributions || {});

		// Sum across all holdings/share classes
		let totalContributions = 0;
		let totalDistributions = 0;
		let totalCapitalAccount = 0;
		let totalReturnsOfCapital = 0;
		const capitalAccountByClass = [];

		// Track which classes we've processed
		const processedClasses = new Set();

		shareClasses.forEach(function (key) {
			processedClasses.add(key);
			const contributions = holdings.contributions[key] || 0;
			const distributions = holdings.distributions[key] || 0;
			// -1 is a sentinel meaning "no data" for this class, treat as 0
			const rawCapitalAccount = holdings.capitalAccount[key];
			const capitalAccount = rawCapitalAccount === -1 || rawCapitalAccount === undefined || rawCapitalAccount === null ? 0 : rawCapitalAccount;
			const returnsOfCapital = holdings.returnsOfCapital?.[key] || 0;

			totalContributions += contributions;
			totalDistributions += distributions;
			totalCapitalAccount += capitalAccount;
			totalReturnsOfCapital += returnsOfCapital;

			// Only include classes with actual data (skip sentinel -1 values)
			if (rawCapitalAccount !== -1) {
				// Per-class total value and TVPI
				const classValue = capitalAccount + distributions + returnsOfCapital;
				const classTvpi = contributions > 0 ? classValue / contributions : 1;

				capitalAccountByClass.push({
					classKey: key,
					className: key,
					value: classValue,
					capitalAccount: capitalAccount,
					contributions: contributions,
					distributions: distributions,
					returnsOfCapital: returnsOfCapital,
					tvpi: classTvpi,
				});
			}
		});

		// Also include distributions and returns of capital from classes not in shareClasses
		// (historical/exited positions). This ensures totals reflect ALL amounts ever received.
		if (holdings.distributions) {
			Object.keys(holdings.distributions).forEach(function (key) {
				if (!processedClasses.has(key) && holdings.distributions[key] > 0) {
					totalDistributions += holdings.distributions[key];
				}
			});
		}
		if (holdings.returnsOfCapital) {
			Object.keys(holdings.returnsOfCapital).forEach(function (key) {
				if (!processedClasses.has(key) && holdings.returnsOfCapital[key] > 0) {
					totalReturnsOfCapital += holdings.returnsOfCapital[key];
				}
			});
		}

		// Core fund calculation: Total Value = Capital Account + Distributions + Returns of Capital
		// RoC is treated as realized value (money returned to investor)
		const totalValue = totalCapitalAccount + totalDistributions + totalReturnsOfCapital;

		// Fund-specific metrics
		// TVPI includes all realized value (distributions + RoC) plus unrealized (capital account)
		const dpi = totalContributions > 0 ? (totalDistributions + totalReturnsOfCapital) / totalContributions : 0;
		const rvpi = totalContributions > 0 ? totalCapitalAccount / totalContributions : 0;
		const tvpi = totalContributions > 0 ? totalValue / totalContributions : 1; // Default to 1x if no contributions

		return {
			totalValue: totalValue,
			totalContributions: totalContributions,
			totalDistributions: totalDistributions,
			totalCapitalAccount: totalCapitalAccount,
			totalReturnsOfCapital: totalReturnsOfCapital,
			breakdown: capitalAccountByClass,
			fundMetrics: {
				pic: totalContributions,
				dpi: dpi,
				rvpi: rvpi,
				tvpi: tvpi,
				moic: tvpi,
			},
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
			},
		};
	},
};

export { FundInvestorCalculator };
